import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  CreatePricingRequest,
  CreatePricingVariantInput,
  CreateProtocolRequest,
  MeteringProfileKind,
  ModelAliasRecord,
  ModelPricingRecord,
  ModelProtocol,
  ModelProtocolRecord,
  ModelRecord,
} from '@nadoutong/contracts';
import {
  KNOWN_METERING_PROFILE_KINDS,
  KNOWN_MODEL_PROTOCOLS,
  defaultMeteringProfileForProtocol,
  isGeminiPricingMode,
  isMeteringProfileAllowedForProtocol,
  isMeteringProfileKind,
  isModelProtocol,
  isUnitMeteredProfileKind,
  tokenPriceCardShapeForProfileKind,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useAddProtocol,
  useCreateModelAlias,
  useCreatePricing,
  useDeleteModelAlias,
  useDeleteModel,
  useDeleteProtocol,
  useModel,
  useModelPricing,
  useModelProtocols,
  useUpdateModel,
} from '../api/useModels.ts';
import { ChannelPricingCard } from '../components/ChannelPricingCard.tsx';
import { DeprecationCard } from '../components/DeprecationCard.tsx';
import {
  EffectiveFromPicker,
  immediateEffectiveFromIso,
  type EffectiveFromMode,
} from '../components/EffectiveFromPicker.tsx';
import { ratedAnchorConflictLatestIso } from '../components/pricingErrors.ts';
import {
  EMPTY_VARIANT_ROW,
  PricingVariantsEditor,
  PricingVariantsTable,
  baseRateAnchorKind,
  baseRateDraftsFrom,
  declaresTokenPriceVariants,
  tokenTierAxisFrom,
  validateVariantDrafts,
  variantDraftsFrom,
  type VariantRowDraft,
} from '../components/PricingVariants.tsx';
import {
  ProtocolDocsFields,
  buildProtocolDocuments,
  emptyProtocolDocs,
  protocolDocsFrom,
  type ProtocolDocsDraft,
} from '../components/ProtocolRegistrationDocs.tsx';
import { UnitPricingCard } from '../components/UnitPricingCard.tsx';
import { formatLocalTimestamp, parseTimestampMs } from '../components/formatters.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  Dialog,
  FormField,
  Input,
  PageHeader,
  Panel,
  Select,
  Skeleton,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * `/supply/models/:vendor/:model` detail view. Card order (owner decision,
 * 2026-08-17: "the profile decides the sales form, so the profile comes
 * first and pricing after it"):
 *   1. Basic info — display_name (inline edit; PATCH on blur or Enter)
 *   2. Aliases
 *   3. Protocols & sales form — the metering-profile registrations that
 *      decide which pricing lanes exist at all
 *   4. Pricing — ONE box for every price surface, sectioned by the
 *      registered profiles' lanes (token history + form; unit generations
 *      when a unit-lane profile is registered)
 *   5. Carrying namespaces & pricing
 *   6. Deprecation, 7. Danger zone
 */

const DECIMAL_PLACES = 6;

function formatRate(value: number): string {
  return `$${value.toFixed(DECIMAL_PLACES)} / Mtok`;
}

function vendorOf(value: string | undefined): string {
  return value ?? '';
}

function isAnthropicVendor(vendor: string): boolean {
  return vendor.toLowerCase() === 'anthropic';
}

function isOpenAiVendor(vendor: string): boolean {
  return vendor.toLowerCase() === 'openai';
}

export function ModelDetail(): JSX.Element {
  const t = useT();
  const params = useParams<{ vendor: string; model: string }>();
  const vendor = vendorOf(params.vendor);
  const model = vendorOf(params.model);
  const detail = useModel(vendor, model);

  if (detail.isPending) {
    return (
      <section className="flex flex-col gap-6" data-testid="model-detail-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <span className="sr-only">{t('model_detail.loading_aria')}</span>
      </section>
    );
  }

  if (detail.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-lg border border-bad-line bg-bad-dim p-6"
        role="alert"
        data-testid="model-detail-error"
      >
        <h2 className="m-0 text-title text-bad-bright">{t('model_detail.error.title')}</h2>
        <p className="m-0 text-body text-bad-bright">
          {detail.error instanceof OpsApiError
            ? `${detail.error.code}: ${detail.error.message}`
            : t('common.network_error')}
        </p>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => void detail.refetch()}>
            {t('common.retry')}
          </Button>
          <Link
            to="/supply/models"
            className="text-label text-paper-mid transition-colors duration-120 hover:text-paper"
          >
            {t('model_detail.back')}
          </Link>
        </div>
      </section>
    );
  }

  return <ModelDetailReady record={detail.data} />;
}

interface ReadyProps {
  readonly record: ModelRecord;
}

function ModelDetailReady({ record }: ReadyProps): JSX.Element {
  const t = useT();
  const pricingQ = useModelPricing(record.vendor, record.model);
  const currentPricing = useMemo(() => {
    if (pricingQ.data === undefined || pricingQ.data.length === 0) return null;
    const now = Date.now();
    const effective = pricingQ.data.filter((p) => {
      const ms = parseTimestampMs(p.effective_from);
      return Number.isFinite(ms) && ms <= now;
    });
    if (effective.length === 0) return null;
    return effective[0] ?? null;
  }, [pricingQ.data]);
  return (
    <section className="flex flex-col gap-6" data-testid="model-detail-page">
      <nav aria-label="breadcrumb" className="flex items-center gap-2 text-label text-paper-low">
        <Link to="/supply/models" className="transition-colors duration-120 hover:text-signal">
          {t('model_detail.breadcrumb.models')}
        </Link>
        <span aria-hidden>/</span>
        <span className="font-mono text-paper-mid">{record.vendor}</span>
        <span aria-hidden>/</span>
        <span className="font-mono font-medium text-paper">{record.model}</span>
      </nav>

      <PageHeader
        title={<span data-testid="model-detail-title">{record.display_name ?? record.model}</span>}
      />
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-dense text-paper-mid">
          {record.vendor}/{record.model}
        </span>
        {record.deleted_at !== null ? (
          <>
            <span aria-hidden className="text-paper-low">·</span>
            <span className="text-dense text-bad">
              {t('model_detail.meta.soft_deleted', { date: record.deleted_at })}
            </span>
          </>
        ) : null}
        {record.deprecated_at !== null ? (
          <>
            <span aria-hidden className="text-paper-low">·</span>
            <Chip tone="warn" testId="model-header-deprecated-chip">
              {t('model_detail.meta.deprecated', { date: record.deprecated_at })}
            </Chip>
          </>
        ) : null}
      </div>

      <BasicInfoCard record={record} />
      <AliasesCard record={record} />
      {/* The profile decides the sales form, so the protocols card leads and
          the pricing box follows it (owner reorder, 2026-08-17): register a
          profile here, and the pricing box below grows the matching lane —
          including the unit region, which renders nothing until a unit-lane
          profile exists (#1669's register-first ordering, unchanged). */}
      <ProtocolsCard vendor={record.vendor} model={record.model} />
      <PricingCard vendor={record.vendor} model={record.model} />
      <ChannelPricingCard vendor={record.vendor} model={record.model} currentPricing={currentPricing} />
      <DeprecationCard record={record} />
      <DangerZone record={record} />
    </section>
  );
}

interface AliasesCardProps {
  readonly record: ModelRecord;
}

function AliasesCard({ record }: AliasesCardProps): JSX.Element {
  const t = useT();
  const create = useCreateModelAlias();
  const remove = useDeleteModelAlias();
  const toast = useToast();
  const [alias, setAlias] = useState('');
  const [error, setError] = useState<string | null>(null);
  const aliases = useMemo<readonly ModelAliasRecord[]>(
    () => [...(record.aliases ?? [])].sort((a, b) => a.alias.localeCompare(b.alias)),
    [record.aliases],
  );
  const aliasSet = useMemo(
    () => new Set(aliases.map((entry) => entry.alias)),
    [aliases],
  );

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = alias.trim();
    if (trimmed.length === 0) {
      setError(t('model_detail.aliases.error.required'));
      return;
    }
    if (trimmed === record.model || aliasSet.has(trimmed)) {
      setError(t('model_detail.aliases.error.duplicate'));
      return;
    }
    try {
      await create.mutateAsync({
        vendor: record.vendor,
        model: record.model,
        body: { alias: trimmed },
      });
      setAlias('');
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  async function handleDelete(aliasName: string): Promise<void> {
    setError(null);
    try {
      await remove.mutateAsync({
        vendor: record.vendor,
        model: record.model,
        alias: aliasName,
      });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Panel testId="model-aliases-card" caption={t('model_detail.aliases.title')} padded>
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense text-paper-low">{t('model_detail.aliases.subtitle')}</p>
        <div className="flex flex-wrap gap-2" data-testid="model-aliases-chips">
          {aliases.length === 0 ? (
            <span className="text-body text-paper-low" data-testid="model-aliases-empty">
              {t('model_detail.aliases.empty')}
            </span>
          ) : null}
          {aliases.map((entry) => (
            <span
              key={entry.alias}
              data-testid={`model-alias-chip-${entry.alias}`}
              className="inline-flex items-center gap-2 rounded-full border border-line bg-raised px-3 py-1 font-mono text-micro text-paper-mid"
            >
              <span>{entry.alias}</span>
              <button
                type="button"
                onClick={() => void handleDelete(entry.alias)}
                disabled={remove.isPending}
                aria-label={t('model_detail.aliases.remove_aria', { alias: entry.alias })}
                data-testid={`model-alias-chip-${entry.alias}-remove`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-paper-low transition-colors duration-120 hover:text-bad focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:opacity-40"
              >
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={handleCreate} noValidate>
          <FormField
            label={t('model_detail.aliases.add.label')}
            fieldId="model-alias-input"
            className="flex-1"
          >
            <Input
              id="model-alias-input"
              type="text"
              mono
              value={alias}
              onChange={(e) => setAlias(e.currentTarget.value)}
              data-testid="model-alias-input"
              disabled={create.isPending}
              placeholder={t('model_detail.aliases.add.placeholder')}
            />
          </FormField>
          <Button
            type="submit"
            variant="primary"
            loading={create.isPending}
            data-testid="model-alias-add"
            className="h-9"
          >
            {create.isPending
              ? t('model_detail.aliases.add.adding')
              : t('model_detail.aliases.add.submit')}
          </Button>
        </form>
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="model-aliases-error"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

interface BasicInfoProps {
  readonly record: ModelRecord;
}

function BasicInfoCard({ record }: BasicInfoProps): JSX.Element {
  const t = useT();
  const update = useUpdateModel();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  async function handlePatch(body: Parameters<typeof update.mutateAsync>[0]['body']): Promise<void> {
    setError(null);
    try {
      await update.mutateAsync({ vendor: record.vendor, model: record.model, body });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Panel
      testId="model-basic-card"
      caption={t('model_detail.basic.title')}
      actions={
        update.isPending ? (
          <span className="text-label text-paper-low">{t('common.saving')}</span>
        ) : undefined
      }
      padded
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <InlineTextField
            label={t('model_detail.basic.display_name')}
            initial={record.display_name ?? ''}
            testId="model-basic-display-name"
            onSubmit={(value) =>
              handlePatch({ display_name: value.length === 0 ? null : value })
            }
          />
        </div>
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="model-basic-error"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

interface InlineTextFieldProps {
  readonly label: string;
  readonly initial: string;
  readonly testId: string;
  readonly onSubmit: (value: string) => Promise<void> | void;
}

function InlineTextField({ label, initial, testId, onSubmit }: InlineTextFieldProps): JSX.Element {
  const [value, setValue] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setValue(initial);
    setDirty(false);
  }, [initial]);

  async function commit(): Promise<void> {
    if (!dirty) return;
    const trimmed = value.trim();
    await onSubmit(trimmed);
    setDirty(false);
  }

  return (
    <FormField label={label} fieldId={testId}>
      <Input
        id={testId}
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          setDirty(true);
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
        }}
        data-testid={testId}
      />
    </FormField>
  );
}

interface PricingProps {
  readonly vendor: string;
  readonly model: string;
}

interface PricingColumn {
  readonly id: string;
  readonly labelKey: I18nKey;
  readonly value: (row: ModelPricingRecord) => number | null | undefined;
  readonly emphasis?: 'primary' | 'muted';
}

// Today as YYYY-MM-DD in the operator's local date, matching
// `<input type="date">` semantics.
function todayYmdLocal(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * The ONE pricing box (owner decision, 2026-08-17): every price surface of
 * this model lives in this Panel, sectioned by the lanes its registered
 * metering profiles sell on. The token region is the incumbent price
 * history + new-generation form, byte-identical for a pure token model; the
 * unit region is the former standalone unit card, rendered only when a
 * unit-lane profile is registered (`UnitPricingCard` keeps that gate). Lane
 * headings appear only when both regions render, so a single-lane model's
 * box carries no extra chrome.
 */
function PricingCard({ vendor, model }: PricingProps): JSX.Element {
  const t = useT();
  const pricing = useModelPricing(vendor, model);
  const protocols = useModelProtocols(vendor, model);
  const hasUnitLane = useMemo(
    () =>
      (protocols.data ?? []).some((p) => isUnitMeteredProfileKind(p.metering_profile)),
    [protocols.data],
  );
  return (
    <Panel
      testId="model-pricing-card"
      caption={t('model_detail.pricing.title')}
      actions={
        pricing.isPending ? (
          <span className="text-label text-paper-low">{t('model_detail.pricing.loading_aria')}</span>
        ) : undefined
      }
      padded
    >
      <div className="flex flex-col gap-6">
        <TokenPricingSection vendor={vendor} model={model} showLaneHeading={hasUnitLane} />
        <UnitPricingCard vendor={vendor} model={model} />
      </div>
    </Panel>
  );
}

interface TokenPricingSectionProps extends PricingProps {
  /** Both lanes render: name this one so the two regions read apart. */
  readonly showLaneHeading: boolean;
}

function TokenPricingSection({ vendor, model, showLaneHeading }: TokenPricingSectionProps): JSX.Element {
  const t = useT();
  const pricing = useModelPricing(vendor, model);
  // Same query key as the protocols card above, so React Query serves both
  // from one fetch. The pricing form needs it because the vendor slug alone
  // does not decide what is billable (see `usesReasoningRate`).
  const protocols = useModelProtocols(vendor, model);
  const create = useCreatePricing();
  const toast = useToast();
  const usesAnthropicCacheTtl = isAnthropicVendor(vendor);
  const usesOpenAiPricing = isOpenAiVendor(vendor);
  const usesLegacyCacheWrite = !usesAnthropicCacheTtl && !usesOpenAiPricing;
  // Reasoning is priced, not merely displayed: without this rate the rating
  // path falls back to legacy pricing and bills thinking tokens at zero.
  //
  // The METERING PROFILE decides this, not the vendor slug (#1902 review). An
  // aggregator reselling Gemini registers under its own slug — `bailian`,
  // `dmx` — and `rating/event-time-pricing.ts` joins `model_metering_profiles`
  // by the REQUEST's protocol, so a gemini-protocol request is charged for
  // reasoning tokens whatever the vendor is called. Asking the slug alone
  // hides the input for exactly the upstreams "onboard a third party with no
  // SQL" is about, and the rate then goes unset.
  //
  // ANY registered gemini profile counts: a request can arrive on any protocol
  // the model is registered for, and the rating path will price it by that
  // registration's profile. `isGeminiPricingMode` is the shared rule from
  // `@nadoutong/contracts` — the same one platform-api's read faces use — so
  // the vendor fallback keeps the one alias table.
  const usesReasoningRate =
    (protocols.data ?? []).some(
      (entry) => entry.metering_profile === 'gemini_generate_content_v1',
    ) || isGeminiPricingMode({ vendor });
  // Image-input is the same class of extra dimension: a generation that
  // omits `image_input_per_mtok_usd` stores NULL, and rating bills those
  // tokens at the text input rate. Console writes are covering new
  // generations, not patches, so leaving this off the form silently drops
  // an ops-cli-configured $8 back to $5.
  //
  // The metering profile decides this, not the vendor slug: a reseller of
  // gpt-image-2 registers `openai_images_v1` under its own name.
  const usesImageInputRate = (protocols.data ?? []).some(
    (entry) => entry.metering_profile === 'openai_images_v1',
  );
  // Until the protocols query succeeds we do not know what this model meters,
  // and the unknown state resolves toward the vendor slug — i.e. toward NOT
  // offering the reasoning rate, which submits a generation that bills thinking
  // tokens at zero. A failed lookup must not be able to produce an
  // under-priced generation, so the write is held rather than silently narrowed.
  //
  // This blocks EVERY vendor, not just Gemini, and that is deliberate. The
  // narrowing that suggests itself — "the slug already tells us anthropic and
  // openai do not meter reasoning, so let those through" — is the exact
  // reasoning the comment above rejects: a reseller registers Gemini under its
  // own slug (`bailian`, `dmx`), and those slugs are indistinguishable from any
  // other non-first-party slug WITHOUT the protocols data we just failed to
  // load. Re-admitting a slug verdict in the one branch where there is no
  // evidence would reopen that hole precisely when it cannot be detected.
  //
  // The cost, stated rather than implied: `queryClient` sets `retry: false`, so
  // a single slow or 500 `/protocols` response holds anthropic and openai
  // pricing entry too, until the operator reloads. That is a real new failure
  // mode on a shared surface. It is accepted because the block is loud (a
  // disabled button plus a named error, and the protocols card below shows its
  // own load error at the same time), reloading clears it, and pricing entry is
  // a deliberate operator action rather than a live traffic path — whereas the
  // failure it prevents is silent and moves money.
  const meteringUnknown = !protocols.isSuccess;
  const cacheReadLabelKey: I18nKey = usesOpenAiPricing
    ? 'model_detail.pricing.column.cache_hit'
    : 'model_detail.pricing.column.cache_read';
  // "From now on" is the default and the only mode a date-granularity input
  // could never express — see EffectiveFromPicker for the write-face rule
  // (full ISO timestamps + the rated-anchor guard) that makes this necessary.
  const [effectiveMode, setEffectiveMode] = useState<EffectiveFromMode>('immediate');
  const [effectiveFrom, setEffectiveFrom] = useState<string>(() => todayYmdLocal());
  const [inputRate, setInputRate] = useState('');
  const [outputRate, setOutputRate] = useState('');
  const [cacheRead, setCacheRead] = useState('');
  const [cacheWrite, setCacheWrite] = useState('');
  const [cacheWrite5m, setCacheWrite5m] = useState('');
  const [cacheWrite1h, setCacheWrite1h] = useState('');
  const [reasoningRate, setReasoningRate] = useState('');
  const [imageInputRate, setImageInputRate] = useState('');
  const [openAiCacheMode, setOpenAiCacheMode] = useState<'hit_only' | 'write_billed'>('hit_only');
  const [error, setError] = useState<string | null>(null);
  // The TOKEN-lane tier axis (#1937). Whether this SKU HAS one is derived from
  // the stored metering spec by the shared `tokenVariantLookupModeFor`, the
  // same rule the platform write face's guard reads — so the form offers the
  // field exactly when the write face requires it.
  const usesPriceVariants = useMemo(
    () => declaresTokenPriceVariants(protocols.data ?? []),
    [protocols.data],
  );
  // The declared tier axis (#1883): with a readable template the tier editor
  // derives each key from per-field inputs instead of a hand-typed string.
  const tierAxis = useMemo(
    () => tokenTierAxisFrom(protocols.data ?? []),
    [protocols.data],
  );
  // Are the base four buckets an ANCHOR rather than a live rate, and why?
  // `'tiered_tokens'` (every token lane tiered — rating matches tiers exactly
  // and never reads the base row), `'unit_lane'` (money moves through
  // `model_unit_pricing`, the token list is never read), or `null` (some
  // token registration prices off the base row, so no anchor claim is true).
  // The note, the history chip and the de-emphasis all key off this verdict.
  const anchorKind = useMemo(
    () => baseRateAnchorKind(protocols.data ?? []),
    [protocols.data],
  );
  // The token price CARD SHAPE the tier axis's profile declares
  // (`TOKEN_PRICE_CARD_SHAPES`). `'single_rate'` (`ark_video_token_v1`): the
  // vendor's card states ONE $/Mtok per tier and no cache counter exists
  // upstream, so the tier surfaces ask/show one price — while STORAGE is
  // unchanged (the price lands in input==output, caches 0), which is exactly
  // the rows operators were hand-equalizing before.
  const variantSingleRate =
    tierAxis !== undefined &&
    tokenPriceCardShapeForProfileKind(tierAxis.profile) === 'single_rate';
  // The BASE row collapses to the one price only while the anchor claim is
  // true (every token registration is tiered): a flat token registration
  // alongside prices its live traffic off the base row's separate buckets,
  // and collapsing them would hide rates that really bill.
  const baseSingleRate = variantSingleRate && anchorKind === 'tiered_tokens';
  const [variantRows, setVariantRows] = useState<readonly VariantRowDraft[]>([EMPTY_VARIANT_ROW]);
  // Seeded from the CURRENT generation's tiers, once they arrive. Not a
  // controlled mirror of the query: after the seed the drafts are the
  // operator's, so a background refetch must not overwrite what they typed.
  // `seededFor` keys the seed to a generation, so navigating between models
  // re-seeds while a refetch of the same one does not.
  const [seededFor, setSeededFor] = useState<string | null>(null);

  const rateColumns = useMemo<readonly PricingColumn[]>(() => {
    if (baseSingleRate) {
      // The single-rate history: one price column, read off the stored input
      // bucket (output is the same figure by the write rule; the cache
      // buckets are structural zeros, not prices, and are not columns).
      return [
        {
          id: 'rate',
          labelKey: 'model_detail.pricing.column.single_rate',
          value: (row) => row.input_per_mtok_usd,
          emphasis: 'primary',
        },
      ];
    }
    if (usesOpenAiPricing) {
      const columns: PricingColumn[] = [
        {
          id: 'input',
          labelKey: 'model_detail.pricing.column.input',
          value: (row) => row.input_per_mtok_usd,
          emphasis: 'primary',
        },
        {
          id: 'cache-read',
          labelKey: cacheReadLabelKey,
          value: (row) => row.cache_read_per_mtok_usd,
        },
        {
          id: 'cache-write',
          labelKey: 'model_detail.pricing.column.cache_write',
          value: (row) => row.cache_write_per_mtok_usd,
        },
        {
          id: 'output',
          labelKey: 'model_detail.pricing.column.output',
          value: (row) => row.output_per_mtok_usd,
          emphasis: 'primary',
        },
      ];
      if (usesImageInputRate) {
        columns.push({
          id: 'image-input',
          labelKey: 'model_detail.pricing.column.image_input',
          value: (row) => row.image_input_per_mtok_usd ?? null,
        });
      }
      return columns;
    }
    const columns: PricingColumn[] = [
      {
        id: 'input',
        labelKey: 'model_detail.pricing.column.input',
        value: (row) => row.input_per_mtok_usd,
        emphasis: 'primary',
      },
      {
        id: 'output',
        labelKey: 'model_detail.pricing.column.output',
        value: (row) => row.output_per_mtok_usd,
        emphasis: 'primary',
      },
      {
        id: 'cache-read',
        labelKey: cacheReadLabelKey,
        value: (row) => row.cache_read_per_mtok_usd,
      },
    ];
    if (usesAnthropicCacheTtl) {
      columns.push(
        {
          id: 'cache-write-5m',
          labelKey: 'model_detail.pricing.column.cache_write_5m',
          value: (row) => row.cache_write_5m_per_mtok_usd ?? row.cache_write_per_mtok_usd,
        },
        {
          id: 'cache-write-1h',
          labelKey: 'model_detail.pricing.column.cache_write_1h',
          value: (row) => row.cache_write_1h_per_mtok_usd ?? null,
        },
      );
      return columns;
    }
    columns.push({
      id: 'cache-write',
      labelKey: 'model_detail.pricing.column.cache_write',
      value: (row) => row.cache_write_per_mtok_usd,
    });
    if (usesReasoningRate) {
      columns.push({
        id: 'reasoning',
        labelKey: 'model_detail.pricing.column.reasoning',
        value: (row) => row.reasoning_per_mtok_usd ?? null,
      });
    }
    if (usesImageInputRate) {
      columns.push({
        id: 'image-input',
        labelKey: 'model_detail.pricing.column.image_input',
        value: (row) => row.image_input_per_mtok_usd ?? null,
      });
    }
    return columns;
  }, [
    baseSingleRate,
    cacheReadLabelKey,
    usesAnthropicCacheTtl,
    usesImageInputRate,
    usesOpenAiPricing,
    usesReasoningRate,
  ]);

  // Pricing list comes back newest-first per contract — the first row is
  // the currently-effective generation (assuming no future-dated rows).
  const sorted = useMemo<ModelPricingRecord[]>(() => pricing.data ?? [], [pricing.data]);
  const currentEffective = useMemo<string | null>(() => {
    const now = Date.now();
    for (const row of sorted) {
      const ts = parseTimestampMs(row.effective_from);
      if (Number.isFinite(ts) && ts <= now) {
        return row.effective_from;
      }
    }
    return null;
  }, [sorted]);

  // Carry the current generation's tiers into the form, once per generation.
  //
  // THIS IS THE ANTI-DATA-LOSS HALF and it is why the guard is survivable:
  // variant rows cascade from the base row, so a new generation inherits none
  // and "reprice a tiered SKU" means restating every tier. Prefilling makes
  // the ordinary edit — same tiers, new rates — the default, instead of
  // sixteen keys retyped from the table above.
  useEffect(() => {
    if (!usesPriceVariants || currentEffective === null || seededFor === currentEffective) return;
    const generation = sorted.find((row) => row.effective_from === currentEffective);
    if (generation === undefined) return;
    setVariantRows(variantDraftsFrom(generation.variants, tierAxis?.template));
    // The BASE buckets are carried forward on a tiered SKU too, and this is
    // the other half of the same anti-data-loss rule: on a tiered generation
    // the base rates price no request (rating matches tiers exactly), yet the
    // write face requires input/output on every generation — so a blank form
    // demands numbers the operator has no way to know, and "only change one
    // tier's price" is refused with `rates_required`. Seeding them from the
    // current generation makes that ordinary edit submittable; the fields stay
    // editable, and a FIRST generation (no current one) still starts blank,
    // because the first anchor is a decision someone has to make once.
    if (tierAxis !== undefined) {
      const base = baseRateDraftsFrom(generation);
      setInputRate(base.input);
      setOutputRate(base.output);
      setCacheRead(base.cacheRead);
      setCacheWrite(base.cacheWrite);
      setCacheWrite5m(base.cacheWrite5m);
      setCacheWrite1h(base.cacheWrite1h);
      setReasoningRate(base.reasoning);
      setImageInputRate(base.imageInput);
      // The openai cache-mode select is derived state over `cache_write`; the
      // read table renders it from the same `> 0` rule. Left at its default it
      // would silently rewrite a carried write-billed generation to hit-only.
      setOpenAiCacheMode(
        generation.cache_write_per_mtok_usd > 0 ? 'write_billed' : 'hit_only',
      );
    }
    setSeededFor(currentEffective);
  }, [currentEffective, seededFor, sorted, tierAxis, usesPriceVariants]);

  function parseRate(raw: string, invalidKey: I18nKey): number | null {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const next = Number(trimmed);
    if (!Number.isFinite(next) || next < 0) {
      throw new Error(t(invalidKey));
    }
    return next;
  }

  function formatOptionalRate(value: number | null | undefined): string {
    return typeof value === 'number' && Number.isFinite(value) ? formatRate(value) : '-';
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (meteringUnknown) {
      // The disabled button is the visible half; this is the half a keyboard
      // submit cannot walk around.
      setError(t('model_detail.pricing.error.metering_unknown'));
      return;
    }
    let isoTimestamp: string;
    if (effectiveMode === 'immediate') {
      // The current instant — always strictly after every already-rated
      // event, so the write face's rated-anchor guard admits it by
      // construction.
      isoTimestamp = immediateEffectiveFromIso();
    } else {
      if (effectiveFrom.trim().length === 0) {
        setError(t('model_detail.pricing.error.effective_from_required'));
        return;
      }
      try {
        // Date-input value (YYYY-MM-DD) -> local midnight ISO-8601.
        const raw = effectiveFrom.trim();
        const parsed =
          raw.includes('T') ? new Date(raw) : new Date(`${raw}T00:00:00`);
        const ts = parsed.getTime();
        if (!Number.isFinite(ts)) {
          setError(t('model_detail.pricing.error.effective_from_invalid'));
          return;
        }
        isoTimestamp = parsed.toISOString();
      } catch {
        setError(t('model_detail.pricing.error.effective_from_invalid'));
        return;
      }
    }
    let inputUsd: number;
    let outputUsd: number;
    let cacheReadUsd: number | null;
    let cacheWriteUsd: number | null;
    let cacheWrite5mUsd: number | null;
    let cacheWrite1hUsd: number | null;
    let reasoningUsd: number | null;
    let imageInputUsd: number | null;
    try {
      if (baseSingleRate) {
        // The one price, into both token buckets; the cache buckets are
        // omitted so the write face stores its zero defaults — the same
        // stored row a hand-equalized chat-shaped submit produced, so the
        // account of every already-billed event is untouched.
        const rate = parseRate(inputRate, 'model_detail.pricing.error.single_rate_invalid');
        if (rate === null) {
          setError(t('model_detail.pricing.error.single_rate_required'));
          return;
        }
        inputUsd = rate;
        outputUsd = rate;
        cacheReadUsd = null;
        cacheWriteUsd = null;
        cacheWrite5mUsd = null;
        cacheWrite1hUsd = null;
        reasoningUsd = null;
        imageInputUsd = null;
      } else {
        const inp = parseRate(inputRate, 'model_detail.pricing.error.input_rate_invalid');
        const out = parseRate(outputRate, 'model_detail.pricing.error.output_rate_invalid');
        if (inp === null || out === null) {
          setError(t('model_detail.pricing.error.rates_required'));
          return;
        }
        inputUsd = inp;
        outputUsd = out;
        cacheReadUsd = parseRate(cacheRead, 'model_detail.pricing.error.cache_read_invalid');
        cacheWriteUsd = usesLegacyCacheWrite || usesOpenAiPricing
          ? parseRate(cacheWrite, 'model_detail.pricing.error.cache_write_invalid')
          : null;
        cacheWrite5mUsd = usesAnthropicCacheTtl
          ? parseRate(cacheWrite5m, 'model_detail.pricing.error.cache_write_5m_invalid')
          : null;
        cacheWrite1hUsd = usesAnthropicCacheTtl
          ? parseRate(cacheWrite1h, 'model_detail.pricing.error.cache_write_1h_invalid')
          : null;
        reasoningUsd = usesReasoningRate
          ? parseRate(reasoningRate, 'model_detail.pricing.error.reasoning_invalid')
          : null;
        imageInputUsd = usesImageInputRate
          ? parseRate(imageInputRate, 'model_detail.pricing.error.image_input_invalid')
          : null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('model_detail.pricing.error.invalid'));
      return;
    }
    if (!baseSingleRate && usesAnthropicCacheTtl && (cacheWrite5mUsd === null || cacheWrite1hUsd === null)) {
      setError(t('model_detail.pricing.error.cache_write_ttl_required'));
      return;
    }
    // Same fail-safe as `meteringUnknown`, on the other half of the same
    // undercharge: `parseRate` maps a blank field to `null`, the body spread
    // below then OMITS `reasoning_per_mtok_usd`, and the generation is written
    // with a NULL reasoning price — which `metering.ts` reads as grounds to
    // drop the whole request to the legacy 4-bucket breakdown, billing thinking
    // at zero. Showing the field was never enough; blank had to stop being a
    // submittable answer. `0` still is one, and says so explicitly.
    if (!baseSingleRate && usesReasoningRate && reasoningUsd === null) {
      setError(t('model_detail.pricing.error.reasoning_required'));
      return;
    }
    if (!baseSingleRate && usesImageInputRate && imageInputUsd === null) {
      setError(t('model_detail.pricing.error.image_input_required'));
      return;
    }
    if (!baseSingleRate && usesOpenAiPricing) {
      if (openAiCacheMode === 'write_billed' && (cacheWriteUsd === null || cacheWriteUsd <= 0)) {
        setError(t('model_detail.pricing.error.cache_write_positive_required'));
        return;
      }
      cacheWriteUsd = openAiCacheMode === 'hit_only' ? 0 : cacheWriteUsd;
    }
    // The TOKEN-lane tiers (#1937). Validated with the SAME contracts
    // constants the platform write face refuses with, so an operator meets a
    // readable message rather than a proxied 400.
    //
    // Submitted ONLY when this SKU declares the axis, and that asymmetry is
    // the equivalence requirement of this addition: an untiered model must
    // keep sending a body with no `variants` key at all, because sending `[]`
    // would be indistinguishable from it to the write face today but is a
    // different statement — and every token SKU in service is untiered.
    let variants: readonly CreatePricingVariantInput[] = [];
    if (usesPriceVariants) {
      const validated = validateVariantDrafts(
        variantRows,
        usesReasoningRate,
        usesImageInputRate,
        tierAxis?.template,
        variantSingleRate,
      );
      if (!validated.ok) {
        setError(t(validated.key, validated.params));
        return;
      }
      variants = validated.variants;
    }
    const body: CreatePricingRequest = {
      effective_from: isoTimestamp,
      input_per_mtok_usd: inputUsd,
      output_per_mtok_usd: outputUsd,
      ...(usesPriceVariants ? { variants: [...variants] } : {}),
      ...(cacheReadUsd !== null ? { cache_read_per_mtok_usd: cacheReadUsd } : {}),
      ...(cacheWriteUsd !== null ? { cache_write_per_mtok_usd: cacheWriteUsd } : {}),
      ...(cacheWrite5mUsd !== null
        ? {
            cache_write_per_mtok_usd: cacheWrite5mUsd,
            cache_write_5m_per_mtok_usd: cacheWrite5mUsd,
          }
        : {}),
      ...(cacheWrite1hUsd !== null ? { cache_write_1h_per_mtok_usd: cacheWrite1hUsd } : {}),
      ...(reasoningUsd !== null ? { reasoning_per_mtok_usd: reasoningUsd } : {}),
      ...(imageInputUsd !== null ? { image_input_per_mtok_usd: imageInputUsd } : {}),
    };
    try {
      await create.mutateAsync({ vendor, model, body });
      setEffectiveMode('immediate');
      setEffectiveFrom(todayYmdLocal());
      setInputRate('');
      setOutputRate('');
      setCacheRead('');
      setCacheWrite('');
      setCacheWrite5m('');
      setCacheWrite1h('');
      setReasoningRate('');
      setImageInputRate('');
      setOpenAiCacheMode('hit_only');
    } catch (err) {
      // The rated-anchor refusal is translated instead of shown as a raw
      // code: it names the latest billed instant and the way out (take
      // effect immediately), which is what the operator can actually act on.
      const anchorLatestIso = ratedAnchorConflictLatestIso(err);
      const message =
        anchorLatestIso !== null
          ? anchorLatestIso.length > 0
            ? t('model_detail.pricing.error.rated_anchor_conflict', {
                latest: formatLocalTimestamp(anchorLatestIso, { seconds: true }),
              })
            : t('model_detail.pricing.error.rated_anchor_conflict_generic')
          : err instanceof OpsApiError
            ? `${err.code}: ${err.message}`
            : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  /**
   * The unit-lane escape from an unanswerable form (owner report,
   * 2026-08-17): the listing gate requires one token pricing generation per
   * model, but on a unit-only SKU no request is ever priced off the token
   * list — the four buckets were a question whose answer moves no money, and
   * operators met it with a $99 journey. One click writes the anchor at ZERO
   * rates, so the stored row itself states "these rates bill nothing".
   * Immediate `effective_from` (the same construction the form's immediate
   * mode uses), so the rated-anchor guard admits it; the cache buckets are
   * omitted so the write face stores its own defaults.
   */
  async function handleCreateUnitAnchor(): Promise<void> {
    setError(null);
    const body: CreatePricingRequest = {
      effective_from: immediateEffectiveFromIso(),
      input_per_mtok_usd: 0,
      output_per_mtok_usd: 0,
    };
    try {
      await create.mutateAsync({ vendor, model, body });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  // ── the new-generation form's fields, composed in TWO orders below ───────
  //
  // On a tiered SKU the tier rates ARE the price and the base buckets are an
  // anchor, so the tier editor leads and the anchor block follows with its
  // explanatory note. On a flat SKU the base buckets are the live price list
  // and the layout stays exactly as it always was. The fields themselves are
  // shared verbatim between the two arms — only the order differs, so the
  // flat form cannot drift from what it renders today.
  const effectiveFromField = (
    <EffectiveFromPicker
      label={t('model_detail.pricing.add.effective_from.label')}
      mode={effectiveMode}
      onModeChange={setEffectiveMode}
      scheduledValue={effectiveFrom}
      onScheduledValueChange={setEffectiveFrom}
      inputType="date"
      disabled={create.isPending}
      inputId="model-pricing-effective-from"
      className="sm:col-span-2"
    />
  );
  // Hidden outright on a single-rate card: there is no cache dimension on
  // the vendor's price card to pick a billing mode FOR.
  const cacheModeField = usesOpenAiPricing && !baseSingleRate ? (
    <FormField
      label={t('model_detail.pricing.add.cache_mode.label')}
      fieldId="model-pricing-cache-mode"
    >
      <Select
        value={openAiCacheMode}
        onValueChange={(next) => {
          if (next === 'hit_only' || next === 'write_billed') setOpenAiCacheMode(next);
        }}
        options={[
          { value: 'hit_only', label: t('model_detail.pricing.cache_mode.hit_only') },
          { value: 'write_billed', label: t('model_detail.pricing.cache_mode.write_billed') },
        ]}
        ariaLabel={t('model_detail.pricing.add.cache_mode.label')}
        testId="model-pricing-cache-mode"
        disabled={create.isPending}
      />
    </FormField>
  ) : null;
  const baseRateFields = baseSingleRate ? (
    // The single-rate anchor: ONE price field, kept on the `input-rate`
    // control (and its draft state) because the input bucket is where the
    // figure is stored and seeded from — submit writes it into both token
    // buckets and omits the cache buckets entirely.
    <FormField
      label={t('model_detail.pricing.add.single_rate.label')}
      fieldId="model-pricing-input-rate"
    >
      <Input
        id="model-pricing-input-rate"
        type="number"
        mono
        min="0"
        step="0.000001"
        value={inputRate}
        onChange={(e) => setInputRate(e.currentTarget.value)}
        data-testid="model-pricing-input-rate"
        disabled={create.isPending}
        placeholder={t('model_detail.pricing.add.single_rate.placeholder')}
      />
    </FormField>
  ) : (
    <>
      <FormField
        label={t('model_detail.pricing.add.input_rate.label')}
        fieldId="model-pricing-input-rate"
      >
        <Input
          id="model-pricing-input-rate"
          type="number"
          mono
          min="0"
          step="0.000001"
          value={inputRate}
          onChange={(e) => setInputRate(e.currentTarget.value)}
          data-testid="model-pricing-input-rate"
          disabled={create.isPending}
          placeholder={t('model_detail.pricing.add.input_rate.placeholder')}
        />
      </FormField>
      <FormField
        label={t('model_detail.pricing.add.output_rate.label')}
        fieldId="model-pricing-output-rate"
      >
        <Input
          id="model-pricing-output-rate"
          type="number"
          mono
          min="0"
          step="0.000001"
          value={outputRate}
          onChange={(e) => setOutputRate(e.currentTarget.value)}
          data-testid="model-pricing-output-rate"
          disabled={create.isPending}
          placeholder={t('model_detail.pricing.add.output_rate.placeholder')}
        />
      </FormField>
      <FormField
        label={
          usesOpenAiPricing
            ? t('model_detail.pricing.add.cache_hit.label')
            : t('model_detail.pricing.add.cache_read.label')
        }
        fieldId="model-pricing-cache-read"
      >
        <Input
          id="model-pricing-cache-read"
          type="number"
          mono
          min="0"
          step="0.000001"
          value={cacheRead}
          onChange={(e) => setCacheRead(e.currentTarget.value)}
          data-testid="model-pricing-cache-read"
          disabled={create.isPending}
          placeholder={
            usesOpenAiPricing
              ? t('model_detail.pricing.add.cache_hit.placeholder')
              : t('model_detail.pricing.add.cache_read.placeholder')
          }
        />
      </FormField>
      {usesAnthropicCacheTtl ? (
        <>
          <FormField
            label={t('model_detail.pricing.add.cache_write_5m.label')}
            fieldId="model-pricing-cache-write-5m"
          >
            <Input
              id="model-pricing-cache-write-5m"
              type="number"
              mono
              min="0"
              step="0.000001"
              value={cacheWrite5m}
              onChange={(e) => setCacheWrite5m(e.currentTarget.value)}
              data-testid="model-pricing-cache-write-5m"
              disabled={create.isPending}
              placeholder={t('model_detail.pricing.add.cache_write_5m.placeholder')}
            />
          </FormField>
          <FormField
            label={t('model_detail.pricing.add.cache_write_1h.label')}
            fieldId="model-pricing-cache-write-1h"
          >
            <Input
              id="model-pricing-cache-write-1h"
              type="number"
              mono
              min="0"
              step="0.000001"
              value={cacheWrite1h}
              onChange={(e) => setCacheWrite1h(e.currentTarget.value)}
              data-testid="model-pricing-cache-write-1h"
              disabled={create.isPending}
              placeholder={t('model_detail.pricing.add.cache_write_1h.placeholder')}
            />
          </FormField>
        </>
      ) : null}
      {usesLegacyCacheWrite || usesOpenAiPricing ? (
        <FormField
          label={t('model_detail.pricing.add.cache_write.label')}
          fieldId="model-pricing-cache-write"
        >
          <Input
            id="model-pricing-cache-write"
            type="number"
            mono
            min="0"
            step="0.000001"
            value={cacheWrite}
            onChange={(e) => setCacheWrite(e.currentTarget.value)}
            data-testid="model-pricing-cache-write"
            disabled={create.isPending || (usesOpenAiPricing && openAiCacheMode === 'hit_only')}
            placeholder={t('model_detail.pricing.add.cache_write.placeholder')}
          />
        </FormField>
      ) : null}
      {usesReasoningRate ? (
        <FormField
          label={t('model_detail.pricing.add.reasoning.label')}
          fieldId="model-pricing-reasoning"
        >
          <Input
            id="model-pricing-reasoning"
            type="number"
            mono
            min="0"
            step="0.000001"
            value={reasoningRate}
            onChange={(e) => setReasoningRate(e.currentTarget.value)}
            data-testid="model-pricing-reasoning"
            disabled={create.isPending}
            placeholder={t('model_detail.pricing.add.reasoning.placeholder')}
          />
        </FormField>
      ) : null}
      {usesImageInputRate ? (
        <FormField
          label={t('model_detail.pricing.add.image_input.label')}
          fieldId="model-pricing-image-input"
        >
          <Input
            id="model-pricing-image-input"
            type="number"
            mono
            min="0"
            step="0.000001"
            value={imageInputRate}
            onChange={(e) => setImageInputRate(e.currentTarget.value)}
            data-testid="model-pricing-image-input"
            disabled={create.isPending}
            placeholder={t('model_detail.pricing.add.image_input.placeholder')}
          />
        </FormField>
      ) : null}
    </>
  );
  // Only ever rendered on the tiered arm: the tiered verdict implies at least
  // one tiered token registration, so a flat SKU can never carry the note.
  // (The unit-lane verdict labels the HISTORY rows and removes the form
  // outright — see the anchor button below.)
  const anchorNote = anchorKind === 'tiered_tokens' ? (
    <p
      className="m-0 text-micro text-paper-low"
      data-testid="model-pricing-base-anchor-note"
    >
      {t('model_detail.pricing.add.base_anchor_note')}
    </p>
  ) : null;

  return (
    <section
      className="flex flex-col gap-4"
      data-testid="model-token-pricing-section"
    >
      {showLaneHeading ? (
        <span
          className="text-body font-medium text-paper"
          data-testid="model-pricing-lane-token"
        >
          {t('model_detail.pricing.lane.token')}
        </span>
      ) : null}
      <div className="flex flex-col gap-4">
        {pricing.isError ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="model-pricing-load-error"
          >
            {pricing.error instanceof OpsApiError
              ? `${pricing.error.code}: ${pricing.error.message}`
              : t('model_detail.pricing.error.load')}
          </p>
        ) : null}
        {pricing.isSuccess && sorted.length === 0 ? (
          <p className="m-0 text-body text-paper-low" data-testid="model-pricing-empty">
            {t('model_detail.pricing.empty')}
          </p>
        ) : null}
        {sorted.length > 0 ? (
          <Table testId="model-pricing-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('model_detail.pricing.column.effective_from')}</Th>
                {rateColumns.map((column) => (
                  <Th key={column.id} num>
                    {t(column.labelKey)}
                  </Th>
                ))}
                {anchorKind !== null ? <Th /> : null}
                {usesOpenAiPricing ? <Th>{t('model_detail.pricing.column.cache_mode')}</Th> : null}
                <Th className="text-right">{t('model_detail.pricing.column.status')}</Th>
              </Tr>
            </THead>
            <TBody>
              {sorted.map((row) => {
                const isCurrent = row.effective_from === currentEffective;
                return (
                  <Tr
                    key={row.effective_from}
                    data-testid={`model-pricing-row-${row.effective_from}`}
                    className={isCurrent ? 'bg-signal-dim/30' : undefined}
                  >
                    <Td mono>
                      {formatLocalTimestamp(row.effective_from, { seconds: true })}
                    </Td>
                    {/* On an anchor-only SKU the history row's base rates are
                        numbers no request is billed by — the real price is
                        the tier table below (tiered) or the unit price list
                        (unit lane). De-emphasized and labelled so a $99
                        anchor wearing the "current" badge stops reading as a
                        live price nobody set. */}
                    {rateColumns.map((column) => (
                      <Td
                        key={column.id}
                        num
                        emphasis={anchorKind === null && column.emphasis === 'primary'}
                      >
                        {formatOptionalRate(column.value(row))}
                      </Td>
                    ))}
                    {anchorKind !== null ? (
                      <Td>
                        <Chip
                          tone="neutral"
                          lamp={false}
                          testId={`model-pricing-anchor-chip-${row.effective_from}`}
                        >
                          {t(
                            anchorKind === 'unit_lane'
                              ? 'model_detail.pricing.row.anchor_unit'
                              : 'model_detail.pricing.row.anchor',
                          )}
                        </Chip>
                      </Td>
                    ) : null}
                    {usesOpenAiPricing ? (
                      <Td>
                        <Chip tone="neutral" lamp={false}>
                          {t(row.cache_write_per_mtok_usd > 0
                            ? 'model_detail.pricing.cache_mode.write_billed'
                            : 'model_detail.pricing.cache_mode.hit_only')}
                        </Chip>
                      </Td>
                    ) : null}
                    <Td className="text-right">
                      {isCurrent ? (
                        <Chip tone="signal" testId="model-pricing-current-badge">
                          {t('model_detail.pricing.row.current')}
                        </Chip>
                      ) : (
                        <span className="text-micro text-paper-low">
                          {t('model_detail.pricing.row.archived')}
                        </span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        ) : null}
        {/* Per-generation tier history: every generation's variant rows ride
            the LIST response, and each renders under the history table with
            its own generation header + current chip. */}
        {sorted.map((row) => (
          <PricingVariantsTable
            key={row.effective_from}
            pricing={row}
            usesReasoningRate={usesReasoningRate}
            usesImageInputRate={usesImageInputRate}
            isCurrent={row.effective_from === currentEffective}
            singleRate={variantSingleRate}
          />
        ))}
        {/* The unit-lane arms (owner report, 2026-08-17): on a unit-only SKU
            the token list prices no request, so the full four-bucket form is
            an unanswerable question and does not render at all. With a
            generation already stored, the history above (anchor chips +
            de-emphasis) is the whole token region; without one, the listing
            gate still requires a token anchor generation, so a one-click
            zero-rate anchor replaces the form. Every other verdict
            (`tiered_tokens` / `null`) keeps the form exactly as it was. */}
        {anchorKind === 'unit_lane' ? (
          pricing.isSuccess && sorted.length === 0 ? (
            <div className="flex flex-col gap-3" data-testid="model-pricing-unit-anchor">
              <p
                className="m-0 text-dense text-paper-low"
                data-testid="model-pricing-unit-anchor-note"
              >
                {t('model_detail.pricing.unit_anchor.note')}
              </p>
              <div>
                <Button
                  type="button"
                  variant="primary"
                  loading={create.isPending}
                  onClick={() => void handleCreateUnitAnchor()}
                  data-testid="model-pricing-unit-anchor-create"
                >
                  {create.isPending
                    ? t('model_detail.pricing.add.submitting')
                    : t('model_detail.pricing.unit_anchor.create')}
                </Button>
              </div>
            </div>
          ) : null
        ) : (
        <form className="flex flex-col gap-4" onSubmit={handleCreate} noValidate>
          <span className="text-label text-paper-mid">
            {t('model_detail.pricing.add.title')}
          </span>
          {usesPriceVariants ? (
            <>
              {/* Tiered: the date, then the TIER rates — the real price —
                  then the anchor block, prefilled and explained. The base
                  buckets leading a tiered form is how the owner met a
                  required field no operator can answer. */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
                {effectiveFromField}
              </div>
              <PricingVariantsEditor
                rows={variantRows}
                onChange={setVariantRows}
                usesReasoningRate={usesReasoningRate}
                usesImageInputRate={usesImageInputRate}
                disabled={create.isPending}
                template={tierAxis?.template}
                profile={tierAxis?.profile}
                singleRate={variantSingleRate}
              />
              {anchorNote}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
                {cacheModeField}
                {baseRateFields}
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
              {effectiveFromField}
              {cacheModeField}
              {baseRateFields}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              loading={create.isPending}
              disabled={meteringUnknown}
              data-testid="model-pricing-submit"
            >
              {create.isPending
                ? t('model_detail.pricing.add.submitting')
                : t('model_detail.pricing.add.submit')}
            </Button>
          </div>
        </form>
        )}
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="model-pricing-error"
          >
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

interface ProtocolsCardProps {
  readonly vendor: string;
  readonly model: string;
}

/**
 * Sentinel for "send no `metering_profile` and let the platform choose".
 * Not a profile kind, so `isMeteringProfileKind` rejects it and the state
 * falls back to null — the two cannot drift apart.
 */
const PROFILE_DEFAULT_VALUE = '__default__';

function ProtocolsCard({ vendor, model }: ProtocolsCardProps): JSX.Element {
  const t = useT();
  const protocols = useModelProtocols(vendor, model);
  const add = useAddProtocol();
  const remove = useDeleteProtocol();
  const toast = useToast();
  // The operator's explicit pick, or `null` for "whatever is addable". Not
  // seeded with a concrete protocol: the registered set is only known after the
  // query resolves, and a fixed initial value is wrong the moment it lands on
  // something already registered — which for a `KNOWN_MODEL_PROTOCOLS[0]`
  // default is the common case, since most models carry their own protocol
  // already. Every operator then met "anthropic is already registered" by
  // pressing Add without touching the control.
  const [selectedRaw, setSelectedRaw] = useState<ModelProtocol | null>(null);
  // `null` is "take the protocol's default", which is what omitting the field
  // means to the platform. Kept distinct from an explicit pick so the operator
  // can see they did not choose.
  const [profile, setProfile] = useState<MeteringProfileKind | null>(null);
  // The registration being RE-REGISTERED, or `null` for a plain add. Editing
  // prefills every stored document into the draft and re-sends all of them,
  // because registration is a whole-row upsert: what the form does not send
  // is cleared (#1944's silent-clear shape, closed here).
  const [editing, setEditing] = useState<ModelProtocolRecord | null>(null);
  // The three configuration documents' draft (#1883).
  const [docs, setDocs] = useState<ProtocolDocsDraft>(emptyProtocolDocs);
  const [error, setError] = useState<string | null>(null);

  // The profile is not free-form per protocol: anthropic and gemini each admit
  // exactly one; `openai` is where `openai_images_v1` lives, and `ark_video_v1`
  // (#1949 A) admits exactly the two task-ingress profiles. Offering the
  // incompatible ones would put a guaranteed 400 in the dropdown.
  const registered = useMemo<readonly ModelProtocolRecord[]>(
    () => protocols.data ?? [],
    [protocols.data],
  );
  const registeredSet = useMemo(
    () => new Set(registered.map((r) => r.protocol)),
    [registered],
  );

  /**
   * Only what can actually be added. A registered protocol is a guaranteed
   * refusal — the write face answers `PROTOCOL_ALREADY_REGISTERED` — so listing
   * it is offering an error, and the empty case means this model is fully
   * covered rather than that something is broken.
   */
  const addableProtocols = useMemo(
    () => KNOWN_MODEL_PROTOCOLS.filter((p) => !registeredSet.has(p)),
    [registeredSet],
  );

  /**
   * Derived, never stored: an explicit pick survives only while it stays
   * addable, so a selection cannot go stale behind a late-arriving list or
   * behind the operator's own successful add.
   */
  const selected: ModelProtocol | null =
    selectedRaw !== null && !registeredSet.has(selectedRaw)
      ? selectedRaw
      : addableProtocols[0] ?? null;

  /** The protocol this form would register: the edited one, or the pick. */
  const activeProtocol: ModelProtocol | null = editing?.protocol ?? selected;

  // The options are the pairing table's own row for the protocol — derived,
  // never restated, so an enum member added in contracts renders its admitted
  // set here without an edit. The task-ingress kinds live on `ark_video_v1` since
  // #1949 A, and are OFFERED (not filtered) since #1883: the form carries
  // editors for the two task documents the write face requires for them.
  const profileOptions = useMemo(
    () => (activeProtocol === null
      ? []
      : KNOWN_METERING_PROFILE_KINDS.filter(
        (kind) => isMeteringProfileAllowedForProtocol(activeProtocol, kind),
      )),
    [activeProtocol],
  );

  /**
   * The platform's default profile for the active protocol, or `null` when
   * the protocol has none and the operator must pick explicitly — `ark_video_v1`
   * (#1949 A): its two profiles are two different billing models, and no
   * default may silently choose one.
   */
  const protocolDefaultProfile: MeteringProfileKind | null =
    activeProtocol === null ? null : defaultMeteringProfileForProtocol(activeProtocol);

  /**
   * What the documents are gated and validated against: the explicit pick, or
   * the default the platform would choose for an omitted `metering_profile`.
   */
  const effectiveProfile: MeteringProfileKind | null = profile ?? protocolDefaultProfile;

  function startEdit(entry: ModelProtocolRecord): void {
    setEditing(entry);
    setProfile(entry.metering_profile);
    setDocs(protocolDocsFrom(entry));
    setError(null);
  }

  function cancelEdit(): void {
    setEditing(null);
    setProfile(null);
    setDocs(emptyProtocolDocs());
    setError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (activeProtocol === null || (editing === null && registeredSet.has(activeProtocol))) {
      // Unreachable through the control, which offers neither state. Kept as
      // the backstop for a stale render racing a just-completed add.
      setError(
        t('model_detail.protocols.error.already_registered', { protocol: activeProtocol ?? '' }),
      );
      return;
    }
    if (effectiveProfile === null) {
      // A protocol without a default (`ark_video_v1`) and no explicit pick: the write
      // face would 400 the omission, so say why here instead of sending it.
      setError(t('model_detail.protocols.profile.required', { protocol: activeProtocol }));
      return;
    }
    const built = buildProtocolDocuments(activeProtocol, effectiveProfile, docs, t);
    if (!built.ok) {
      setError(built.message);
      return;
    }
    const body: CreateProtocolRequest = {
      protocol: activeProtocol,
      // Omitted when the operator left it on the default, so the platform
      // decides rather than this form pinning a value it merely displayed.
      ...(profile === null ? {} : { metering_profile: profile }),
      ...built.documents,
    };
    try {
      if (editing !== null) {
        // The write face refuses POST on a live registration, so re-register
        // is deregister-then-register. The form still holds every document,
        // so a failure between the two steps loses nothing: the error is
        // shown and re-submitting completes the registration.
        try {
          await remove.mutateAsync({ vendor, model, protocol: activeProtocol });
        } catch (err) {
          if (!(err instanceof OpsApiError && err.code === 'PROTOCOL_NOT_REGISTERED')) {
            throw err;
          }
        }
      }
      await add.mutateAsync({ vendor, model, body });
      setProfile(null);
      setDocs(emptyProtocolDocs());
      setEditing(null);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  async function handleDelete(protocol: ModelProtocol): Promise<void> {
    setError(null);
    try {
      await remove.mutateAsync({ vendor, model, protocol });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Panel
      testId="model-protocols-card"
      caption={t('model_detail.protocols.title')}
      actions={
        protocols.isPending ? (
          <span className="text-label text-paper-low">{t('model_detail.protocols.loading_aria')}</span>
        ) : undefined
      }
      padded
    >
      <div className="flex flex-col gap-4">
        {protocols.isError ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="model-protocols-load-error"
          >
            {protocols.error instanceof OpsApiError
              ? `${protocols.error.code}: ${protocols.error.message}`
              : t('model_detail.protocols.error.load')}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2" data-testid="model-protocols-chips">
          {registered.length === 0 && !protocols.isPending ? (
            <span className="text-body text-paper-low" data-testid="model-protocols-empty">
              {t('model_detail.protocols.empty')}
            </span>
          ) : null}
          {registered.map((entry) => (
            <span
              key={entry.protocol}
              data-testid={`model-protocols-chip-${entry.protocol}`}
              className="inline-flex items-center gap-2 rounded-full border border-line bg-raised px-3 py-1 font-mono text-micro text-paper-mid"
            >
              <span>{entry.protocol}</span>
              {/* The profile, not just the protocol. Two models on `openai` can
                  meter as text or as images, which decides both the price and
                  whether the image lane admits them at all (#1557); a chip that
                  showed only the protocol left that invisible. */}
              <span
                data-testid={`model-protocols-chip-${entry.protocol}-profile`}
                className="text-paper-low"
              >
                {entry.metering_profile ?? t('model_detail.protocols.profile.unset')}
              </span>
              {/* Whether a declarative metering spec is STORED on this
                  registration (#1878). Not the spec itself — this surface
                  cannot edit one yet, and the form deliberately sends no
                  `metering_spec`, which the write face reads as "clear it".
                  So a deregister-then-reregister here silently destroys a
                  stored spec, and an operator who cannot see that a spec
                  exists cannot know they are about to. The marker is the
                  cheapest thing that makes the loss visible; the editor lands
                  with the parameter surface. */}
              <span
                data-testid={`model-protocols-chip-${entry.protocol}-spec`}
                className="text-paper-low"
              >
                {entry.metering_spec
                  ? t('model_detail.protocols.spec.present')
                  : t('model_detail.protocols.spec.absent')}
              </span>
              {/* Re-register with the stored documents prefilled. This is the
                  only console path that does not silently clear a stored spec
                  or task document: the form re-sends everything it read. */}
              <button
                type="button"
                onClick={() => startEdit(entry)}
                disabled={add.isPending || remove.isPending}
                data-testid={`model-protocols-chip-${entry.protocol}-edit`}
                className="text-micro text-paper-low transition-colors duration-120 hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:opacity-40"
              >
                {t('model_detail.protocols.edit')}
              </button>
              <button
                type="button"
                onClick={() => void handleDelete(entry.protocol)}
                disabled={remove.isPending}
                aria-label={t('model_detail.protocols.remove_aria', { protocol: entry.protocol })}
                data-testid={`model-protocols-chip-${entry.protocol}-remove`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-paper-low transition-colors duration-120 hover:text-bad focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:opacity-40"
              >
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        {editing === null && selected === null && !protocols.isPending ? (
          <p
            className="m-0 text-body text-paper-low"
            data-testid="model-protocols-all-registered"
          >
            {t('model_detail.protocols.all_registered')}
          </p>
        ) : (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          {editing !== null ? (
            <span className="text-label text-paper-mid" data-testid="model-protocols-edit-title">
              {t('model_detail.protocols.edit_title', { protocol: editing.protocol })}
            </span>
          ) : null}
          <div className="flex items-end gap-3">
            {editing !== null ? (
              <FormField
                label={t('model_detail.protocols.add.label')}
                fieldId="model-protocols-editing-protocol"
                className="flex-1"
              >
                <span
                  id="model-protocols-editing-protocol"
                  className="inline-flex h-9 items-center font-mono text-body text-paper"
                  data-testid="model-protocols-editing-protocol"
                >
                  {editing.protocol}
                </span>
              </FormField>
            ) : (
              <FormField
                label={t('model_detail.protocols.add.label')}
                fieldId="model-protocols-select"
                className="flex-1"
              >
                <Select
                  value={selected ?? undefined}
                  onValueChange={(next) => {
                    if (isModelProtocol(next)) {
                      setSelectedRaw(next);
                    }
                  }}
                  options={addableProtocols.map((p) => ({ value: p, label: p }))}
                  mono
                  disabled={add.isPending}
                  ariaLabel={t('model_detail.protocols.add.label')}
                  testId="model-protocols-select"
                />
              </FormField>
            )}
            <FormField
              label={t('model_detail.protocols.profile.label')}
              fieldId="model-protocols-profile"
              className="flex-1"
            >
              <Select
                // Remounted whenever the protocol (and with it the option
                // set) changes: the select primitive resets a controlled
                // value to '' when its value and its option list move in one
                // commit, which is exactly what entering edit mode does.
                key={`${editing === null ? 'add' : 'edit'}-${activeProtocol ?? 'none'}`}
                // A protocol without a platform default (`ark_video_v1`, #1949 A) has
                // no "default" option to fall back to: the control shows a
                // placeholder until the operator names a profile, and submit
                // refuses until they do — mirroring the write face, which
                // 400s an omitted `metering_profile` for such a protocol.
                value={profile ?? (protocolDefaultProfile === null ? undefined : PROFILE_DEFAULT_VALUE)}
                placeholder={t('model_detail.protocols.profile.pick')}
                onValueChange={(next) => {
                  if (next.length === 0) return;
                  setProfile(isMeteringProfileKind(next) ? next : null);
                }}
                options={[
                  ...(protocolDefaultProfile === null
                    ? []
                    : [{
                        value: PROFILE_DEFAULT_VALUE,
                        label: t('model_detail.protocols.profile.default', {
                          profile: protocolDefaultProfile,
                        }),
                      }]),
                  ...profileOptions.map((kind) => ({ value: kind, label: kind })),
                ]}
                mono
                disabled={add.isPending || remove.isPending}
                ariaLabel={t('model_detail.protocols.profile.label')}
                testId="model-protocols-profile"
              />
            </FormField>
            <Button
              type="submit"
              variant="primary"
              loading={add.isPending || (editing !== null && remove.isPending)}
              data-testid="model-protocols-add"
              className="h-9"
            >
              {editing !== null
                ? t('model_detail.protocols.edit_submit')
                : add.isPending
                  ? t('model_detail.protocols.add.adding')
                  : t('model_detail.protocols.add.submit')}
            </Button>
            {editing !== null ? (
              <Button
                type="button"
                variant="ghost"
                onClick={cancelEdit}
                disabled={add.isPending || remove.isPending}
                data-testid="model-protocols-edit-cancel"
                className="h-9"
              >
                {t('common.cancel')}
              </Button>
            ) : null}
          </div>
          {effectiveProfile !== null ? (
            <ProtocolDocsFields
              profile={effectiveProfile}
              draft={docs}
              onChange={setDocs}
              disabled={add.isPending || remove.isPending}
            />
          ) : null}
        </form>
        )}
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="model-protocols-error"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

interface DangerZoneProps {
  readonly record: ModelRecord;
}

function DangerZone({ record }: DangerZoneProps): JSX.Element {
  const t = useT();
  const remove = useDeleteModel();
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setError(null);
    try {
      await remove.mutateAsync({ vendor: record.vendor, model: record.model });
      setConfirmOpen(false);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Panel
      testId="model-danger-zone"
      caption={<span className="text-bad">{t('model_detail.danger.title')}</span>}
      className="border-bad-line"
      padded
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-body text-paper-low">{t('model_detail.danger.body')}</p>
        <div>
          <Button
            variant="danger"
            onClick={() => setConfirmOpen(true)}
            data-testid="model-delete"
            disabled={record.deleted_at !== null}
          >
            {record.deleted_at !== null
              ? t('model_detail.danger.already_deleted')
              : t('model_detail.danger.delete')}
          </Button>
        </div>
        {confirmOpen ? (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next && !remove.isPending) {
                setConfirmOpen(false);
                setError(null);
              }
            }}
            title={t('model_detail.delete_modal.title', {
              vendor: record.vendor,
              model: record.model,
            })}
            closeLabel={t('common.close')}
            size="sm"
            testId="model-delete-confirm"
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (!remove.isPending) {
                      setConfirmOpen(false);
                      setError(null);
                    }
                  }}
                  disabled={remove.isPending}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => void handleConfirm()}
                  loading={remove.isPending}
                  data-testid="model-detail-delete-confirm"
                >
                  {remove.isPending ? t('common.deleting') : t('common.delete')}
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-3">
              <p className="m-0 text-body text-paper-mid">{t('model_detail.delete_modal.body')}</p>
              {error !== null ? (
                <p
                  className="m-0 text-label text-bad"
                  role="alert"
                  data-testid="model-detail-delete-error"
                >
                  {error}
                </p>
              ) : null}
            </div>
          </Dialog>
        ) : null}
      </div>
    </Panel>
  );
}
